import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { CalendarDays, Plane, Car, Sparkles, AlertTriangle, ArrowUpRight, Bed, Hourglass, CheckCircle2, XCircle, BarChart3, BrushCleaning } from "lucide-react";
import { format, parseISO, differenceInDays } from "date-fns";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { useAuth } from "../context/AuthContext";

const VILLA_BG = "https://customer-assets.emergentagent.com/job_villa-stays-3/artifacts/d1di2xrq_IMG_5360.jpg";

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [requests, setRequests] = useState([]);
  const [days, setDays] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => {
    (async () => {
      const [s, b, r] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/bookings"),
        api.get("/requests"),
      ]);
      setSummary(s.data);
      setBookings(b.data);
      setRequests(r.data);
    })().catch(console.error);
  }, []);

  useEffect(() => {
    api.get(`/dashboard/days-per-person?year=${year}`).then((r) => setDays(r.data)).catch(console.error);
  }, [year]);

  const today = new Date().toISOString().slice(0, 10);
  const [showMine, setShowMine] = useState(false);
  const upcomingAll = bookings.filter((b) => b.status === "confirmed" && b.approval_status !== "rejected" && b.departure_date >= today);
  const upcoming = (showMine ? upcomingAll.filter((b) => b.is_my_stay) : upcomingAll).slice(0, 5);
  const pendingApprovals = bookings.filter((b) => b.approval_status === "pending" && b.status === "confirmed").slice(0, 5);
  const pendingReqs = requests.filter((r) => r.status === "pending").slice(0, 6);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Buenos días";
    if (h < 19) return "Buenas tardes";
    return "Buenas noches";
  })();

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl border border-[#E8E1D5]">
        <img src={VILLA_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2B2B2B]/80 via-[#2B2B2B]/40 to-transparent" />
        <div className="relative p-8 sm:p-12 text-[#FDFBF7]">
          <p className="text-xs uppercase tracking-[0.3em] opacity-80">Hola Familia</p>
          <h1 className="font-serif-villa text-4xl sm:text-5xl mt-2 leading-tight">
            {greeting}, {user?.name?.split(" ")[0] || "amigo"}.
          </h1>
          <p className="mt-3 text-sm sm:text-base opacity-90 max-w-xl">
            A quiet overview of la casa — who's coming, what needs doing, and the plan ahead.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/bookings/new"
              data-testid="cta-new-booking"
              className="inline-flex items-center gap-2 villa-btn-primary px-5 py-2.5 rounded-full text-sm"
            >
              Plan a stay <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/calendar"
              data-testid="cta-calendar"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm bg-[#FDFBF7]/15 border border-[#FDFBF7]/30 hover:bg-[#FDFBF7]/25 transition-colors"
            >
              View calendar
            </Link>
          </div>
        </div>
      </section>

      {/* Stat bento */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Upcoming stays" value={summary?.upcoming_count ?? "—"} icon={CalendarDays} testid="stat-upcoming" />
        <StatCard
          label={user?.is_admin ? "Awaiting your approval" : "Awaiting approval"}
          value={summary?.pending_approvals ?? "—"}
          icon={Hourglass}
          tone={summary?.pending_approvals ? "warn" : "default"}
          testid="stat-approvals"
        />
        <StatCard
          label={`Days occupied · ${summary?.current_year ?? new Date().getFullYear()}`}
          value={summary?.occupied_days_year ?? "—"}
          icon={CalendarDays}
          testid="stat-occupied-days"
        />
        <StatCard
          label="Overlaps"
          value={summary?.conflicts_count ?? "—"}
          icon={AlertTriangle}
          tone={summary?.conflicts_count ? "warn" : "default"}
          testid="stat-conflicts"
        />
      </section>

      {/* Admin approvals panel */}
      {user?.is_admin && pendingApprovals.length > 0 && (
        <section className="villa-card p-6 sm:p-8" data-testid="approvals-panel">
          <div className="mb-4">
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A]">Aprobaciones pendientes</p>
            <h2 className="font-serif-villa text-2xl">Awaiting your approval</h2>
          </div>
          <ul className="divide-y divide-[#E8E1D5]">
            {pendingApprovals.map((b) => (
              <li key={b.booking_id} className="py-3 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <Link to={`/bookings/${b.booking_id}`} className="font-serif-villa text-lg hover:text-[#C25E3E]" data-testid={`approval-${b.booking_id}`}>
                    {b.title || "Family stay"}{b.primary_guest_name ? ` (${b.primary_guest_name})` : ""}
                  </Link>
                  <div className="text-xs text-[#7A7A7A] mt-0.5">
                    {format(parseISO(b.arrival_date), "d MMM")} → {format(parseISO(b.departure_date), "d MMM")}
                    <span className="mx-2">·</span>requested by {b.created_by_email || "someone"}
                  </div>
                </div>
                <Link to={`/bookings/${b.booking_id}`} className="text-sm text-[#C25E3E] hover:underline">Review →</Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* 2 col */}
      <section className="grid lg:grid-cols-3 gap-6">
        {/* Upcoming stays */}
        <div className="lg:col-span-2 villa-card p-6 sm:p-8" data-testid="upcoming-list">
          <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
            <div>
              <p className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A]">Próximas reservas</p>
              <h2 className="font-serif-villa text-2xl">Upcoming stays</h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowMine(false)}
                data-testid="upcoming-filter-all"
                className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-wider transition-colors ${
                  !showMine ? "bg-[#2B2B2B] text-[#FDFBF7]" : "bg-[#FDFBF7] border border-[#E8E1D5] text-[#2B2B2B] hover:bg-[#F5F0E6]"
                }`}
              >All</button>
              <button
                onClick={() => setShowMine(true)}
                data-testid="upcoming-filter-mine"
                className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-wider transition-colors ${
                  showMine ? "bg-[#2B2B2B] text-[#FDFBF7]" : "bg-[#FDFBF7] border border-[#E8E1D5] text-[#2B2B2B] hover:bg-[#F5F0E6]"
                }`}
              >My stays</button>
              <Link to="/calendar" className="text-sm text-[#C25E3E] hover:underline ml-2">See all →</Link>
            </div>
          </div>
          {upcoming.length === 0 ? (
            <EmptyState text={showMine ? "Nothing of yours coming up." : "Nothing booked yet — la casa está tranquila."} />
          ) : (
            <ul className="divide-y divide-[#E8E1D5]">
              {upcoming.map((b) => (
                <li key={b.booking_id} className="py-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <Link
                      to={`/bookings/${b.booking_id}`}
                      data-testid={`upcoming-${b.booking_id}`}
                      className="font-serif-villa text-lg text-[#2B2B2B] hover:text-[#C25E3E]"
                    >
                      {b.title || "Family stay"}{b.primary_guest_name ? ` (${b.primary_guest_name})` : ""}
                    </Link>
                    <div className="text-sm text-[#7A7A7A] mt-0.5">
                      {format(parseISO(b.arrival_date), "EEE d MMM")} → {format(parseISO(b.departure_date), "EEE d MMM")}
                      <span className="mx-2">·</span>
                      {differenceInDays(parseISO(b.departure_date), parseISO(b.arrival_date))} nights
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {b.approval_status === "pending" && (
                      <span className="villa-chip villa-chip-warn">Awaiting approval</span>
                    )}
                    {b.car_required && (
                      <span className="villa-chip"><Car className="w-3 h-3 inline mr-1" /> Car</span>
                    )}
                    <span className={`villa-chip ${b.cost_category === "friends" ? "" : "villa-chip-ok"}`}>
                      {b.cost_category}
                    </span>
                    {b.conflicts?.length > 0 && (
                      <span className="villa-chip villa-chip-warn" data-testid={`conflict-chip-${b.booking_id}`}>
                        Conflict
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Pending requests */}
        <div className="villa-card p-6 sm:p-8" data-testid="pending-list">
          <div className="mb-5">
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A]">Pendientes</p>
            <h2 className="font-serif-villa text-2xl">Action items</h2>
          </div>

          {/* Housekeeping bookings */}
          {summary?.housekeeping_action_items?.length > 0 && (
            <div className="mb-5" data-testid="hk-action-items">
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A] mb-2 inline-flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> Casa · housekeeping
              </div>
              <ul className="space-y-2">
                {summary.housekeeping_action_items.map((h) => (
                  <li key={h.booking_id} className="flex items-start justify-between gap-3 p-3 rounded-xl border border-[#E8E1D5] bg-[#FBF7EF]" data-testid={`hk-action-${h.booking_id}`}>
                    <div className="min-w-0">
                      <Link to={`/bookings/${h.booking_id}`} className="text-sm text-[#2B2B2B] hover:text-[#C25E3E] inline-flex items-center gap-2">
                        {h.title}
                        {h.ref_number && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FDFBF7] border border-[#E8E1D5] text-[#6B705C]">{h.ref_number}</span>}
                      </Link>
                      <div className="text-xs text-[#7A7A7A]">
                        {format(parseISO(h.arrival_date), "d MMM")} → {format(parseISO(h.departure_date), "d MMM")}
                      </div>
                    </div>
                    <HkStatusPill status={h.status} />
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Car bookings */}
          {summary?.car_action_items?.length > 0 && (
            <div className="mb-5" data-testid="car-action-items">
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A] mb-2 inline-flex items-center gap-1.5">
                <Car className="w-3 h-3" /> Coche · car bookings
              </div>
              <ul className="space-y-2">
                {summary.car_action_items.map((c) => (
                  <li key={c.owner_booking_id} className="flex items-start justify-between gap-3 p-3 rounded-xl border border-[#E8E1D5] bg-[#FBF7EF]" data-testid={`car-action-${c.owner_booking_id}`}>
                    <div className="min-w-0">
                      <Link to={`/bookings/${c.owner_booking_id}`} className="text-sm text-[#2B2B2B] hover:text-[#C25E3E]">{c.title}</Link>
                      <div className="text-xs text-[#7A7A7A]">
                        {format(parseISO(c.pickup_date), "d MMM")} → {format(parseISO(c.dropoff_date), "d MMM")}
                        {c.members_count > 1 && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#6B705C]">· spans {c.members_count} stays</span>}
                      </div>
                    </div>
                    <CarStatusPill status={c.status} />
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Other (housekeeping) */}
          {pendingReqs.length === 0 && !summary?.car_action_items?.length ? (
            <EmptyState text="All clear — nothing waiting." />
          ) : pendingReqs.length > 0 && (
            <ul className="space-y-3">
              {pendingReqs.map((r) => (
                <li key={r.request_id} className="flex items-start gap-3">
                  <RequestIcon type={r.type} />
                  <div className="min-w-0">
                    <div className="text-sm capitalize text-[#2B2B2B]">{r.type.replace("_", " ")}</div>
                    <div className="text-xs text-[#7A7A7A]">
                      {format(parseISO(r.scheduled_date), "EEE d MMM")} · {r.booking?.title || "stay"}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <Link to="/operations" className="mt-5 inline-block text-sm text-[#C25E3E] hover:underline">
            Open Operations →
          </Link>
        </div>
      </section>

      {/* Days per person chart */}
      <section className="villa-card p-6 sm:p-8" data-testid="days-chart-panel">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
          <div>
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A] inline-flex items-center gap-2">
              <BarChart3 className="w-3 h-3" /> Días en la casa
            </p>
            <h2 className="font-serif-villa text-2xl mt-1">Days in the casa · {year}</h2>
            <p className="text-sm text-[#7A7A7A]">How many days each person has booked this year (family + friends).</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {(days?.available_years || [year]).map((y) => (
              <button
                key={y}
                onClick={() => setYear(y)}
                data-testid={`year-${y}`}
                className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-wider transition-colors ${
                  y === year ? "bg-[#2B2B2B] text-[#FDFBF7]" : "bg-[#FDFBF7] border border-[#E8E1D5] text-[#2B2B2B] hover:bg-[#F5F0E6]"
                }`}
              >{y}</button>
            ))}
          </div>
        </div>
        {(!days?.items || days.items.length === 0) ? (
          <EmptyState text="No stays this year yet — la casa está tranquila." />
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={days.items} margin={{ top: 8, right: 12, left: -8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E1D5" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#2B2B2B" }} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis tick={{ fontSize: 12, fill: "#7A7A7A" }} allowDecimals={false} />
                <Tooltip
                  cursor={{ fill: "rgba(194,94,62,0.06)" }}
                  contentStyle={{ background: "#FDFBF7", border: "1px solid #E8E1D5", borderRadius: 12, fontFamily: "Outfit, sans-serif" }}
                  formatter={(v) => [`${v} ${v === 1 ? "day" : "days"}`, "Booked"]}
                />
                <Bar dataKey="days" radius={[8, 8, 0, 0]}>
                  {days.items.map((it) => (
                    <Cell key={it.person_id || it.name} fill={it.relation === "friend" ? "#457B9D" : "#C25E3E"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>

      {/* Cleaning spend YTD (admin only) */}
      {user?.is_admin && summary?.cleaning_spend_year && (
        <section className="villa-card p-6 sm:p-8" data-testid="cleaning-spend-panel">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
            <div>
              <p className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A] inline-flex items-center gap-2">
                <BrushCleaning className="w-3 h-3" /> Limpieza · gasto
              </p>
              <h2 className="font-serif-villa text-2xl mt-1">Cleaning spend · {summary.current_year}</h2>
              <p className="text-sm text-[#7A7A7A]">Cleaner bills in <strong>EUR</strong>; you pay in <strong>GBP</strong> at the rate on the day. Cancelled stays excluded.</p>
            </div>
            <div className="text-right space-y-1">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#7A7A7A]">Billed (EUR)</div>
                <div className="font-serif-villa text-2xl text-[#2B2B2B]" data-testid="cleaning-spend-eur-total">€{Number(summary.cleaning_spend_year.eur_total ?? summary.cleaning_spend_year.total ?? 0).toFixed(0)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#7A7A7A]">Paid (GBP)</div>
                <div className="font-serif-villa text-3xl text-[#C25E3E]" data-testid="cleaning-spend-gbp-total">£{Number(summary.cleaning_spend_year.gbp_total ?? 0).toFixed(0)}</div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <SpendCard label="Check-in cleans" eur={summary.cleaning_spend_year.eur_check_in ?? summary.cleaning_spend_year.check_in} gbp={summary.cleaning_spend_year.gbp_check_in} testid="spend-checkin" />
            <SpendCard label="Check-out cleans" eur={summary.cleaning_spend_year.eur_check_out ?? summary.cleaning_spend_year.check_out} gbp={summary.cleaning_spend_year.gbp_check_out} testid="spend-checkout" />
            <SpendCard label="Laundry" eur={summary.cleaning_spend_year.eur_laundry ?? summary.cleaning_spend_year.laundry} gbp={summary.cleaning_spend_year.gbp_laundry} testid="spend-laundry" />
          </div>
        </section>
      )}
    </div>
  );
}

function SpendCard({ label, eur, gbp, testid }) {
  return (
    <div className="bg-[#FBF7EF] border border-[#E8E1D5] rounded-2xl p-5" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.2em] text-[#7A7A7A]">{label}</div>
      <div className="font-serif-villa text-2xl text-[#2B2B2B] mt-1">€{Number(eur || 0).toFixed(0)}</div>
      <div className="text-xs text-[#7A7A7A] mt-1">paid <span className="text-[#C25E3E] font-medium">£{Number(gbp || 0).toFixed(0)}</span></div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone = "default", testid }) {
  return (
    <div className="villa-card p-5 flex items-start justify-between" data-testid={testid}>
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#7A7A7A]">{label}</div>
        <div className={`font-serif-villa text-3xl mt-2 ${tone === "warn" && Number(value) > 0 ? "text-[#C25E3E]" : "text-[#2B2B2B]"}`}>
          {value}
        </div>
      </div>
      <Icon className={`w-5 h-5 ${tone === "warn" && Number(value) > 0 ? "text-[#C25E3E]" : "text-[#6B705C]"}`} strokeWidth={1.5} />
    </div>
  );
}

function RequestIcon({ type }) {
  const map = { cleaning: Sparkles, laundry: Bed, bed_prep: Bed, car: Car };
  const Icon = map[type] || Sparkles;
  return (
    <div className="w-9 h-9 rounded-full bg-[#FBF7EF] border border-[#E8E1D5] flex items-center justify-center shrink-0">
      <Icon className="w-4 h-4 text-[#6B705C]" strokeWidth={1.6} />
    </div>
  );
}

function EmptyState({ text }) {
  return <div className="text-sm text-[#9A9A9A] italic py-4">{text}</div>;
}

function CarStatusPill({ status }) {
  const map = {
    to_order: { label: "To order", cls: "bg-[#FBE9E7] text-[#A84C30] border-[#F2C9B6]" },
    waiting_confirmation: { label: "Waiting", cls: "bg-[#FDFBF7] text-[#6B705C] border-[#E8E1D5]" },
    booked: { label: "Booked", cls: "bg-[#E6F0E8] text-[#3F6B4F] border-[#C9DAC9]" },
    cancelled: { label: "Cancelled", cls: "bg-[#FBE9E7] text-[#A84C30] border-[#F2C9B6] opacity-60" },
  };
  const m = map[status] || map.to_order;
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border whitespace-nowrap ${m.cls}`}>
      {m.label}
    </span>
  );
}

function HkStatusPill({ status }) {
  const map = {
    to_send: { label: "To send", cls: "bg-[#FBE9E7] text-[#A84C30] border-[#F2C9B6]" },
    sent: { label: "Sent", cls: "bg-[#FDFBF7] text-[#6B705C] border-[#E8E1D5]" },
    confirmed: { label: "Confirmed", cls: "bg-[#E6F0E8] text-[#3F6B4F] border-[#C9DAC9]" },
    cancelled: { label: "Cancelled", cls: "bg-[#FBE9E7] text-[#A84C30] border-[#F2C9B6] opacity-60" },
  };
  const m = map[status] || map.to_send;
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border whitespace-nowrap ${m.cls}`}>
      {m.label}
    </span>
  );
}
